import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Star, ShieldCheck, ArrowRight, Sparkles, TrendingUp, HelpCircle, ChevronDown, ChevronLeft, ChevronRight, Quote, Calendar, Ruler, MessageSquare, FileText } from 'lucide-react';
import { COMPANY_INFO, HERO_USPS, fetchCollection, MOCK_TESTIMONIALS, HOMEPAGE_FAQS } from '../data';
import FormSection from '../components/FormSection';
import DynamicScrollbar from '../components/DynamicScrollbar';
import AnimationVideo from '../components/AnimationVideo';

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [openFaqId, setOpenFaqId] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    async function loadCmsData() {
      const fetchedTestimonials = await fetchCollection('testimonials');
      const fetchedFaqs = await fetchCollection('faqs');
      
      setTestimonials(fetchedTestimonials && fetchedTestimonials.length > 0 ? fetchedTestimonials : MOCK_TESTIMONIALS);
      // Brug den opdaterede FAQ med 5 relevante spørgsmål og 3-5 linjers svar
      setFaqs(HOMEPAGE_FAQS);
    }
    loadCmsData();
  }, []);

  useEffect(() => {
    if (testimonials.length === 0 || isHovering) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials, isHovering]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 lg:py-20 overflow-hidden mb-0">
        {/* Background Video */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="https://assets.auxodev.dk/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/modern-danish-home-windows.jpg"
            title="Baggrundsvideo: Moderne hjem med energieffektive ruder"
            aria-label="Baggrundsvideo med energieffektive ruder i moderne bolig"
            className="w-full h-full object-cover"
          >
            <source src="https://assets.auxodev.dk/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energiruder-intro-video.mp4" type="video/mp4" />
          </video>
          {/* Overlay to match section's design (light gradient) */}
          <div className="absolute inset-0 bg-gradient-to-b from-brand-light/90 via-white/85 to-white/95 mix-blend-normal" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left Column: Value Proposition */}
            <div className="lg:col-span-7 space-y-6 text-left">
              {/* Social Proof Badge */}
              <a
                href={COMPANY_INFO.trustpilotUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white border border-brand-light px-3.5 py-1.5 rounded-full shadow-sm hover:shadow-md transition-all group"
              >
                <span className="flex items-center gap-1 text-brand-green font-bold text-sm">
                  <Star className="w-4 h-4 fill-brand-green" />
                  Trustpilot
                </span>
                <span className="text-brand-dark/40 font-bold text-xs">•</span>
                <span className="text-brand-dark/80 text-xs font-semibold group-hover:text-brand-blue transition-colors">
                  {COMPANY_INFO.trustpilotRating} ud af 5 stjerner ({COMPANY_INFO.trustpilotCount} anmeldelser)
                </span>
              </a>

              {/* Headline */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-deep leading-tight tracking-tight">
                Skift dine gamle termoruder
                <span className="block mt-1.5">Få nye energitermoruder og spar penge på varmen</span>
              </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-brand-dark/80 leading-relaxed max-w-2xl">
                <strong className="font-semibold text-brand-deep">Energirude Eksperten, din glarmester i Egedal, Nordsjælland og store dele af Sjælland</strong> – med base i Smørum udskifter vi dine gamle termoruder og hjælper dig med at finde den rigtige løsning til dit hjem eller din ejendom – med gratis besøg, personlig rådgivning og fokus på energibesparelse.
              </p>

              {/* Mobile Trustpilot Image */}
              <div className="block lg:hidden pt-1">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=90,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energirude-eksperten-glarmestre-firmabil.jpg" 
                  alt="Glarmestre fra Energirude Eksperten med anbefalingsbanner foran firmabil" 
                  className="w-full sm:max-w-[320px] h-auto object-contain"
                />
              </div>

              {/* 4 USPs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                {HERO_USPS.map((usp, idx) => (
                  <div key={idx} className="flex items-center gap-2.5">
                    <span className="bg-brand-green/10 text-brand-green p-1 rounded-full border border-brand-green/20 shrink-0">
                      <Check className="w-4 h-4" />
                    </span>
                    <span className="text-sm sm:text-base font-semibold text-brand-deep">
                      {usp}
                    </span>
                  </div>
                ))}
              </div>

              {/* Trustpilot Visual Widget */}
              <div className="pt-4 border-t border-brand-light grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
                {/* Trustpilot Image (Desktop) */}
                <div className="hidden lg:flex shrink-0 justify-start">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=90,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energirude-eksperten-glarmestre-firmabil.jpg" 
                    alt="Glarmestre fra Energirude Eksperten med anbefalingsbanner foran firmabil" 
                    className="w-full sm:max-w-[320px] h-auto object-contain"
                  />
                </div>

                <div className="flex flex-col gap-2 text-xs text-brand-dark/60">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-brand-green text-brand-green" />
                    ))}
                    <span className="font-bold text-brand-deep ml-1">Fremragende</span>
                  </div>
                  <p className="leading-relaxed">
                    Vores kunder elsker os på Trustpilot og håndværker.dk. <span className="font-semibold text-brand-deep">Gratis besøg og rådgivning</span> er altid uforpligtende.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: CTA Form */}
            <div className="lg:col-span-5">
              <FormSection />
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Scrollbar */}
      <DynamicScrollbar />

      {/* Rådgivning & Glarmestergaranti */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 mt-4 sm:mt-10 lg:mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
          <div className="lg:col-span-6 flex flex-col">
            <div className="relative overflow-hidden shadow-xl border border-brand-light flex-1 min-h-[300px] lg:min-h-0 h-full">
              <video
                src="https://assets.auxodev.dk/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energirude-eksperten-raadgivning-video.mp4#t=0.001"
                controls
                playsInline
                preload="metadata"
                title="Video: Energirude Eksperten rådgivning og montering af ruder"
                aria-label="Video om Energirude Ekspertens rådgivning og glarmesterarbejde"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6 space-y-6 flex flex-col justify-center">
            <span className="text-xs font-bold tracking-widest text-brand-blue uppercase bg-brand-light px-3 py-1 rounded-full">
              Rådgivning & Tryghed
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-deep leading-tight">
              Få rådgivning og hjælp af din glarmester
            </h2>
            <p className="text-sm sm:text-base text-brand-dark/80 leading-relaxed">
              Hos Energirude Eksperten står vi gerne til rådighed, hvis du har spørgsmål eller brug for rådgivning. Det kan være alt fra professionel <Link to="/udskiftning-af-ruder" className="text-brand-deep hover:text-brand-blue font-semibold underline decoration-brand-blue/30 underline-offset-2 transition-colors">udskiftning af ruder</Link> og udskiftning af en punkteret <Link to="/termoruder" className="text-brand-deep hover:text-brand-blue font-semibold underline decoration-brand-blue/30 underline-offset-2 transition-colors">termorude</Link> til rådgivning om opgradering til <Link to="/energiruder" className="text-brand-deep hover:text-brand-blue font-semibold underline decoration-brand-blue/30 underline-offset-2 transition-colors">energiruder</Link> samt skræddersyede <Link to="/glasloesninger" className="text-brand-deep hover:text-brand-blue font-semibold underline decoration-brand-blue/30 underline-offset-2 transition-colors">glasløsninger</Link> i en hel ejendom.
            </p>
            <p className="text-sm sm:text-base text-brand-dark/80 leading-relaxed">
              Hvis du står med en forsikringsskade, hjælper vi som glarmester dig gerne med at udfylde din skadesanmeldelse. I de fleste tilfælde kan Energirude Eksperten afregne direkte med forsikringsselskabet, og vi samarbejder med alle forsikringsselskaber.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 sm:mt-12 lg:mt-20">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-xs font-bold tracking-widest text-brand-blue uppercase bg-brand-light px-3 py-1 rounded-full">
            Hvorfor vælge os
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-deep mt-3">
            Derfor vælger kunder Energirude Eksperten
          </h2>
          <p className="text-sm sm:text-base text-brand-dark/70 mt-2">
            Med over 25 års erfaring i glarmesterbranchen leverer vi professionelt arbejde, der sikrer dig mod varmetab og høje varmeregninger.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-brand-warm/30 border border-brand-light p-6 sm:p-8 rounded-2xl space-y-4">
            <div className="bg-brand-light p-3 rounded-xl inline-block text-brand-blue">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-brand-deep">Glarmestergaranti & Tryghed</h3>
            <p className="text-sm sm:text-base text-brand-dark/80 leading-relaxed">
              Vi giver dig fuld garanti på det udførte arbejde. Vi yder desuden 5 års garanti på alle vores ruder.
            </p>
          </div>

          <div className="bg-brand-warm/30 border border-brand-light p-6 sm:p-8 rounded-2xl space-y-4">
            <div className="bg-brand-light p-3 rounded-xl inline-block text-brand-blue">
              <TrendingUp className="w-6 h-6" />
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-brand-deep">Mærkbare Energibesparelser</h3>
            <p className="text-sm sm:text-base text-brand-dark/80 leading-relaxed">
              Vores moderne lavenergiruder isolerer helt op til 40% bedre end gamle, utætte ruder. Det kan mærkes direkte på din varmeregning fra dag ét.
            </p>
          </div>

          <div className="bg-brand-warm/30 border border-brand-light p-6 sm:p-8 rounded-2xl space-y-4">
            <div className="bg-brand-light p-3 rounded-xl inline-block text-brand-blue">
              <Sparkles className="w-6 h-6" />
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-brand-deep">Kun ruder, ikke karme</h3>
            <p className="text-sm sm:text-base text-brand-dark/80 leading-relaxed">
              Som autoriseret glarmester kan vi i de fleste tilfælde nøjes med at udskifte selve ruden og bevare dine eksisterende karme. Det gør løsningen langt billigere og mere skånsom for boligen.
            </p>
          </div>
        </div>
      </section>

      {/* Erfaren glarmester i Egedal, Nordsjælland og store dele af Sjælland */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 space-y-6">
            <span className="text-xs font-bold tracking-widest text-brand-blue uppercase bg-brand-light px-3 py-1 rounded-full">
              Erfaren glarmester i Egedal, Nordsjælland og store dele af Sjælland
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-deep leading-tight">
              Søger du en glarmester med erfaring og høj faglighed?
            </h2>
            <p className="text-sm sm:text-base text-brand-dark/80 leading-relaxed font-semibold text-brand-deep">
              Så kontakt Energirude Eksperten. Vi har base i Smørum, mere end 25 års erfaring inden for glarmesterbranchen, og har til stadighed fokus på kundeservice og høj kvalitet.
            </p>
            <p className="text-sm sm:text-base text-brand-dark/80 leading-relaxed">
              Energirude Eksperten er etableret i 1999 og har med base i Smørum oparbejdet en stor kundekreds i Egedal, Nordsjælland og store dele af Sjælland. Gennem årene har vi arbejdet med alt inden for glarmesterbranchen, og vi sørger for løbende at videreuddanne os. Energiruder er et område, hvor der sker meget spændende produktudvikling, og hos Energirude Eksperten har vi fod på alle de nyeste tiltag.
            </p>
            <div className="pt-4">
              <Link
                to="/om-os"
                className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-deep text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-sm"
              >
                Læs mere om os <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative rounded-2xl overflow-hidden shadow-xl border border-brand-light">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energirude-eksperten-rabat-kampagnebrochure.jpg"
                alt="Energirude Eksperten kampagnebrochure med 20% rabat på lavenergi-termoruder"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/20 to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Savings Impact Section */}
      <section className="bg-brand-light/50 py-16 border-y border-brand-light mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-bold tracking-widest text-brand-blue uppercase bg-brand-light px-3 py-1 rounded-full">
                Besparelsesfokus
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-deep">
                Så meget kan nye energiruder betyde for din økonomi
              </h2>
              <p className="text-sm sm:text-base text-brand-dark/80 leading-relaxed">
                Vidste du, at op mod 35% af en boligs varmetab sker gennem vinduerne? Når vores glarmester opgraderer dine gamle termoruder til moderne lavenergiruder med varm kant, reducerer du varmetabet markant og skærer toppen af varmeregningen.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="text-brand-green font-bold mt-0.5">✔</span>
                  <p className="text-sm sm:text-base text-brand-dark/80">
                    <strong>Lavere U-værdi:</strong> Gammel termorude har en U-værdi på ca. 3,0 – en ny tolags energirude har en U-værdi på helt ned til 1,1.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-brand-green font-bold mt-0.5">✔</span>
                  <p className="text-sm sm:text-base text-brand-dark/80">
                    <strong>Mindre kondens:</strong> Varm kant minimerer kuldebroer og reducerer risikoen for dug og skimmelsvamp langs kanterne.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-brand-green font-bold mt-0.5">✔</span>
                  <p className="text-sm sm:text-base text-brand-dark/80">
                    <strong>Bedre indeklima:</strong> Slip for træk og kuldenedfald, så du kan udnytte pladsen helt tæt på vinduerne.
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="bg-white p-6 sm:p-8 rounded-2xl border border-brand-light shadow-lg text-center space-y-6">
                <h3 className="text-lg sm:text-xl font-bold text-brand-deep">Beregn dit potentiale</h3>
                <p className="text-xs sm:text-sm text-brand-dark/70">
                  Et gennemsnitligt dansk parcelhus sparer typisk mellem <span className="font-bold text-brand-green">3.000 og 6.000 kr.</span> årligt på varmeregningen ved at skifte til energiruder.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-brand-warm/30 p-4 rounded-xl border border-brand-light">
                    <span className="block text-xs text-brand-dark/50 uppercase font-semibold">Gammel rude</span>
                    <span className="block text-xl font-bold text-red-500 mt-1">Højt varmetab</span>
                  </div>
                  <div className="bg-brand-light/40 p-4 rounded-xl border border-brand-blue/10">
                    <span className="block text-xs text-brand-dark/50 uppercase font-semibold">Ny energirude</span>
                    <span className="block text-xl font-bold text-brand-green mt-1">Op til 40% sparet</span>
                  </div>
                </div>
                <Link
                  to="/gratis-besoeg"
                  className="block w-full text-center bg-brand-blue hover:bg-brand-deep text-white py-3 rounded-lg font-semibold transition-all duration-200"
                >
                  Få beregnet din besparelse her
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Energiruder & En glarmester der hjælper dig med at spare */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 space-y-6">
            <span className="text-xs font-bold tracking-widest text-brand-blue uppercase bg-brand-light px-3 py-1 rounded-full">
              Energibesparelse
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-deep leading-tight">
              Energiruder, der sænker dit varmeforbrug
            </h2>
            <p className="text-sm sm:text-base text-brand-dark/80 leading-relaxed font-semibold text-brand-deep">
              Med moderne energiruder kan du reducere varmetabet markant og samtidig forbedre både komfort og indeklima i din bolig.
            </p>
            <p className="text-sm sm:text-base text-brand-dark/80 leading-relaxed">
              Hos Energirudeeksperten leverer og monterer vi som erfaren glarmester energiruder i høj kvalitet med fokus på god isolering, flot finish og lang holdbarhed i Egedal, Nordsjælland og store dele af Sjælland. Med base i Smørum er vi hurtigt fremme hos dig. En professionel glarmesterudskiftning til energiruder betaler sig – både på varmeregningen og i den daglige komfort.
            </p>

            <div className="pt-4 space-y-4">
              <h3 className="text-xl font-bold text-brand-deep">En glarmester der hjælper dig med at spare</h3>
              <p className="text-sm sm:text-base text-brand-dark/80 leading-relaxed">
                Som glarmester favner vi bredt hos Energirude Eksperten, men en af vores helt store fokuspunkter er energiruder, som hjælper vores kunder med at reducere energitab og varmeomkostninger.
              </p>
              <p className="text-sm sm:text-base text-brand-dark/80 leading-relaxed">
                Vores glarmester tilbyder energiruder til meget fordelagtige priser, og er naturligvis eksperter i præcis opmåling og professionel montering, uanset hvilken type bolig eller ejendom de skal isættes i.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-brand-warm/40 border border-brand-light p-6 sm:p-8 rounded-2xl space-y-6 shadow-sm">
              <h3 className="text-lg sm:text-xl font-bold text-brand-deep">Opgaver vi løser inden for glarmesterfaget:</h3>
              <ul className="space-y-3">
                {[
                  { name: "Energiruder", href: "/energiruder" },
                  { name: "Termoruder", href: "/termoruder" },
                  { name: "Udskiftning af ruder", href: "/udskiftning-af-ruder" },
                  { name: "Glasløsninger & butiksruder", href: "/glasloesninger" },
                  { name: "Forsikringsskader", href: "/kontakt" }
                ].map((service, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="bg-brand-blue/10 text-brand-blue p-1 rounded-full shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                    <Link
                      to={service.href}
                      className="text-sm sm:text-base font-semibold text-brand-deep hover:text-brand-blue transition-colors flex items-center gap-1.5 group"
                    >
                      <span>{service.name}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-brand-blue opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="text-xs sm:text-sm text-brand-dark/60 italic border-t border-brand-light/80 pt-4">
                *Kun ruder ikke karme.*
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How a Free Visit Works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs font-bold tracking-widest text-brand-blue uppercase bg-brand-light px-3 py-1">
            Processen
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-deep mt-3">
            Sådan foregår et gratis besøg
          </h2>
          <p className="text-sm sm:text-base text-brand-dark/70 mt-2">
            Det er nemt, hurtigt og fuldstændig uforpligtende at få besøg af vores glarmester.
          </p>
        </div>

        <div className="relative">
          {/* Horizontal connecting line on desktop */}
          <div className="hidden lg:block absolute top-1/2 left-12 right-12 h-0.5 bg-brand-light -translate-y-16 -z-10" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {/* Step 1 */}
            <div className="relative bg-white border border-brand-light border-t-4 border-t-brand-blue p-6 sm:p-8 flex flex-col justify-between">
              <span className="absolute top-4 right-6 text-5xl font-black text-brand-blue/10 select-none">
                01
              </span>
              <div>
                <div className="w-12 h-12 bg-brand-light flex items-center justify-center text-brand-blue mb-6">
                  <Calendar className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-brand-deep">Book nemt online</h3>
                <p className="text-sm text-brand-dark/70 leading-relaxed mt-2">
                  Udfyld vores korte formular eller ring til os på tlf. 21 82 80 00 for at aftale en tid.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative bg-white border border-brand-light border-t-4 border-t-brand-blue p-6 sm:p-8 flex flex-col justify-between">
              <span className="absolute top-4 right-6 text-5xl font-black text-brand-blue/10 select-none">
                02
              </span>
              <div>
                <div className="w-12 h-12 bg-brand-light flex items-center justify-center text-brand-blue mb-6">
                  <Ruler className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-brand-deep">Glarmesteren måler op</h3>
                <p className="text-sm text-brand-dark/70 leading-relaxed mt-2">
                  Vores glarmester kommer hjem til dig, tjekker dine nuværende ruder og foretager en præcis opmåling af glassene.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative bg-white border border-brand-light border-t-4 border-t-brand-blue p-6 sm:p-8 flex flex-col justify-between">
              <span className="absolute top-4 right-6 text-5xl font-black text-brand-blue/10 select-none">
                03
              </span>
              <div>
                <div className="w-12 h-12 bg-brand-light flex items-center justify-center text-brand-blue mb-6">
                  <MessageSquare className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-brand-deep">Personlig rådgivning</h3>
                <p className="text-sm text-brand-dark/70 leading-relaxed mt-2">
                  Vi rådgiver dig om de bedste glastyper (lyd, sol, energi) og beregner dit besparelsespotentiale.
                </p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="relative bg-white border border-brand-light border-t-4 border-t-brand-blue p-6 sm:p-8 flex flex-col justify-between">
              <span className="absolute top-4 right-6 text-5xl font-black text-brand-blue/10 select-none">
                04
              </span>
              <div>
                <div className="w-12 h-12 bg-brand-light flex items-center justify-center text-brand-blue mb-6">
                  <FileText className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-brand-deep">Uforpligtende tilbud</h3>
                <p className="text-sm text-brand-dark/70 leading-relaxed mt-2">
                  Du modtager et skræddersyet, prisstærkt tilbud. Det er helt op til dig, om du ønsker at takke ja.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-xs font-bold tracking-widest text-brand-blue uppercase bg-brand-light px-3 py-1 rounded-full">
            Vores Ydelser
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-deep mt-3">
            Professionelle glarmesterløsninger
          </h2>
          <p className="text-sm sm:text-base text-brand-dark/70 mt-2">
            Som din lokale glarmester er vi eksperter i alt inden for ruder og glas. Vi udskifter ruder med præcision i alle typer boliger og ejendomme.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Service 1 */}
          <Link
            to="/energiruder"
            className="bg-white border border-brand-light rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col justify-between group text-left"
          >
            <div className="p-6 space-y-3">
              <h3 className="text-lg font-bold text-brand-deep group-hover:text-brand-blue transition-colors">Energiruder</h3>
              <p className="text-xs sm:text-sm text-brand-dark/70 leading-relaxed">
                Fokus på energibesparelse, bedre indeklima, mindre varmetab og moderne rudevalg.
              </p>
            </div>
            <div className="p-6 pt-0">
              <span className="text-brand-blue font-semibold text-sm flex items-center gap-1">
                Læs mere <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </Link>

          {/* Service 2 */}
          <Link
            to="/udskiftning-af-ruder"
            className="bg-white border border-brand-light rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col justify-between group text-left"
          >
            <div className="p-6 space-y-3">
              <h3 className="text-lg font-bold text-brand-deep group-hover:text-brand-blue transition-colors">Udskiftning af ruder</h3>
              <p className="text-xs sm:text-sm text-brand-dark/70 leading-relaxed">
                Hurtig vurdering af din glarmester, professionel montering og rådgivning om den mest optimale løsning til dine vinduer.
              </p>
            </div>
            <div className="p-6 pt-0">
              <span className="text-brand-blue font-semibold text-sm flex items-center gap-1">
                Læs mere <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </Link>

          {/* Service 3 */}
          <Link
            to="/termoruder"
            className="bg-white border border-brand-light rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col justify-between group text-left"
          >
            <div className="p-6 space-y-3">
              <h3 className="text-lg font-bold text-brand-deep group-hover:text-brand-blue transition-colors">Termoruder</h3>
              <p className="text-xs sm:text-sm text-brand-dark/70 leading-relaxed">
                Fokus på dug, punkterede ruder, isoleringsevne og udskiftning til bedre alternativer.
              </p>
            </div>
            <div className="p-6 pt-0">
              <span className="text-brand-blue font-semibold text-sm flex items-center gap-1">
                Læs mere <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </Link>

          {/* Service 4 */}
          <Link
            to="/glasloesninger"
            className="bg-white border border-brand-light rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col justify-between group text-left"
          >
            <div className="p-6 space-y-3">
              <h3 className="text-lg font-bold text-brand-deep group-hover:text-brand-blue transition-colors">Glasløsninger</h3>
              <p className="text-xs sm:text-sm text-brand-dark/70 leading-relaxed">
                Fokus på specialløsninger, rådgivning og funktionelle glasvalg.
              </p>
            </div>
            <div className="p-6 pt-0">
              <span className="text-brand-blue font-semibold text-sm flex items-center gap-1">
                Læs mere <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="bg-brand-warm/30 py-16 border-y border-brand-light mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="text-xs font-bold tracking-widest text-brand-blue uppercase bg-brand-light px-3 py-1 rounded-full">
                Kundeudtalelser
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-deep mt-3">
                Hvad siger vores kunder om os?
              </h2>
              <p className="text-sm sm:text-base text-brand-dark/70 mt-2">
                Vi er stolte af vores høje kundetilfredshed. Læs et udvalg af anmeldelser fra vores glade kunder.
              </p>
            </div>

            {/* Slideshow Container */}
            <div
              className="relative bg-white p-8 sm:p-12 rounded-3xl border border-brand-light shadow-lg space-y-6 overflow-hidden"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              {/* Quote Icon Background */}
              <div className="absolute top-6 right-8 text-brand-light/40 pointer-events-none">
                <Quote className="w-24 h-24 stroke-[1.5]" />
              </div>

              {/* Active Slide */}
              <div className="min-h-[180px] flex flex-col justify-between relative z-10">
                <div className="space-y-4">
                  <div className="flex items-center gap-1">
                    {[...Array(testimonials[currentSlide].rating || 5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-brand-green text-brand-green" />
                    ))}
                  </div>
                  <div
                    className="text-base sm:text-lg md:text-xl text-brand-deep leading-relaxed italic font-medium prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: testimonials[currentSlide].text }}
                  />
                </div>

                <div className="pt-6 border-t border-brand-light/80 flex items-center justify-between mt-6">
                  <div>
                    <span className="block font-bold text-brand-deep text-base sm:text-lg">
                      {testimonials[currentSlide].name}
                    </span>
                    <span className="text-xs text-brand-dark/50 font-semibold">
                      Verificeret kunde
                    </span>
                  </div>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="absolute top-1/2 -translate-y-1/2 left-2 right-2 flex justify-between pointer-events-none">
                <button
                  onClick={prevSlide}
                  className="p-2 rounded-full bg-white/90 border border-brand-light text-brand-deep hover:bg-brand-blue hover:text-white hover:border-brand-blue transition-all duration-200 shadow-md pointer-events-auto focus:outline-none"
                  aria-label="Forrige anmeldelse"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextSlide}
                  className="p-2 rounded-full bg-white/90 border border-brand-light text-brand-deep hover:bg-brand-blue hover:text-white hover:border-brand-blue transition-all duration-200 shadow-md pointer-events-auto focus:outline-none"
                  aria-label="Næste anmeldelse"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Dot Indicators */}
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    currentSlide === idx
                      ? 'bg-brand-blue w-6'
                      : 'bg-brand-light hover:bg-brand-blue/40'
                  }`}
                  aria-label={`Gå til anmeldelse ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs font-bold tracking-widest text-brand-blue uppercase bg-brand-light px-3 py-1 rounded-full">
              FAQ
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-deep mt-3">
              Ofte stillede spørgsmål
            </h2>
            <p className="text-sm sm:text-base text-brand-dark/70 mt-2">
              Få svar på de mest almindelige spørgsmål om energiruder, dug og isolering.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
            {faqs.slice(0, 6).map((faq) => {
              const isOpen = openFaqId === faq.id;
              return (
                <div
                  key={faq.id}
                  className="bg-white border border-brand-light rounded-xl overflow-hidden transition-all"
                >
                  <button
                    onClick={() => setOpenFaqId(isOpen ? null : faq.id)}
                    className="flex items-center justify-between w-full p-5 text-left font-bold text-brand-deep hover:text-brand-blue transition-colors gap-4"
                  >
                    <span className="text-sm sm:text-base">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-brand-blue shrink-0 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 pt-0 border-t border-brand-light/50">
                      <div
                        className="text-sm sm:text-base text-brand-dark/80 leading-relaxed pt-4 prose max-w-none"
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-center pt-8">
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 text-brand-blue hover:text-brand-deep font-bold transition-colors text-sm sm:text-base"
            >
              Se alle ofte stillede spørgsmål <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* Bottom CTA Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 mt-20">
        <div className="bg-brand-deep text-white rounded-3xl pt-16 pb-8 px-6 sm:p-12 lg:p-16 relative border border-brand-blue/20 shadow-xl text-center space-y-6">
          {/* Organic Blue Badge Eyecatcher */}
          <div className="absolute -top-10 right-4 sm:-right-6 w-32 h-32 sm:w-44 sm:h-44 z-10 drop-shadow-lg animate-hop">
            <svg viewBox="0 0 200 200" className="w-full h-full text-brand-blue fill-current">
              <path d="M165,100 C165,135 135,165 100,165 C65,165 35,135 35,100 C35,65 65,35 100,35 C135,35 165,65 165,100 Z" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white p-2 sm:p-3">
              <span className="text-[9px] sm:text-xs uppercase tracking-widest font-extrabold text-brand-light/90">Slå til nu</span>
              <span className="text-sm sm:text-lg font-black leading-none my-1">OG SPAR</span>
              <span className="text-2xl sm:text-4xl font-black leading-none text-yellow-300 drop-shadow-sm">20%</span>
            </div>
          </div>

          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(36,119,199,0.15),transparent_60%)] pointer-events-none rounded-3xl" />
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-3xl mx-auto leading-tight relative z-10">
            Klar til at spare på varmeregningen? Book dit gratis besøg i dag!
          </h2>
          <p className="text-sm sm:text-base text-brand-light/80 max-w-2xl mx-auto relative z-10">
            Få besøg af vores erfarne glarmester, der med base i Smørum kører ud i Egedal, Nordsjælland og store dele af Sjælland til en præcis opmåling og rådgivning om den mest optimale løsning til din bolig. Det er 100% gratis og uforpligtende.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 relative z-10">
            <Link
              to="/gratis-besoeg"
              className="w-full sm:w-auto bg-brand-blue hover:bg-white hover:text-brand-deep text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 shadow-md"
            >
              Book gratis besøg nu
            </Link>
            <a
              href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, '')}`}
              className="w-full sm:w-auto border border-white/20 hover:bg-white/10 text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2"
            >
              Ring på {COMPANY_INFO.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
